// TODO(adshidtadka): スマート申請用のGqlResultを@graffer-inc/smart-applyに定義して
// このコンソール基盤用のGqlResultrはCloudGovErrorCodeLiteralsに依存するように変更する
import { SmartApplyErrorCodeLiterals } from "../";
import { Query } from "./generated/types";
import { reportUnexpectedError } from "../";

import type { GraphQLFormattedError } from "graphql/error/GraphQLError";

/**
 * NOTE(adshidtadka): SmartApplyApiErrorに依存しておりCloudGovApiには依存していない
 */
export type GrafferGqlError = GraphQLFormattedError & {
  extensions?: {
    code: SmartApplyErrorCodeLiterals;
    status: number;
  };
};

export class GrafferGqlErrors {
  constructor(
    readonly errors: GrafferGqlError[],
    readonly rawError: Error,
  ) {}

  findErrorFor(
    rootPath: keyof Omit<Query, "__typename">,
  ): GrafferGqlError | undefined {
    return this.errors.find((e) => (e.path ? e.path[0] === rootPath : false));
  }
}

/**
 * NOTE(adshidtadka): SmartApplyApiErrorに依存しておりCloudGovApiには依存していない
 */
export class GrafferGqlResult<T> {
  constructor(
    readonly data?: T | undefined,
    readonly error?: GrafferGqlErrors,
  ) {}

  /**
   * Queryの実行結果に応じて、戻り値を変更するための関数
   *
   * 期待するErrorCodeがない場合は、空文字を渡す
   */
  matchResult<ExpectedErrorCode extends SmartApplyErrorCodeLiterals | "", R>(
    errorConfig: {
      // クエリに対してエラーがある場合に期待されるpath
      rootPath?: keyof Omit<Query, "__typename">;
      // 期待しうるエラーコード
      errorTypes?: ExpectedErrorCode;
    },
    matcher: {
      success: (data: T) => R;
      loading: R;
    } & (ExpectedErrorCode extends ""
      ? { unexpected: R }
      : { [e in ExpectedErrorCode | "unexpected"]: R }),
  ) {
    if (this.data === undefined && !this.error) {
      return matcher.loading;
    }
    if (!this.error) {
      return matcher.success(this.data!);
    }
    // 予期しないエラー状態の場合の処理
    if (this.error.errors.length > 1 || !errorConfig.rootPath) {
      reportUnexpectedError(this.error.rawError);
      return matcher["unexpected"];
    }
    const foundError = this.error.findErrorFor(errorConfig.rootPath);
    if (!foundError || !errorConfig.errorTypes) {
      reportUnexpectedError(this.error.rawError);
      return matcher["unexpected"];
    }
    return (
      matcher as {
        [e in ExpectedErrorCode | "unexpected"]: R;
      }
    )[foundError.extensions!.code as ExpectedErrorCode];
  }

  /**
   * 取得したデータの型を別の型に変換したパターンのオブジェクトを返却する
   */
  convert<R>(factory: (data: T) => R): GrafferGqlResult<R> {
    if (!this.data) {
      return new GrafferGqlResult<R>(undefined, this.error);
    }
    return new GrafferGqlResult<R>(factory(this.data), this.error);
  }

  matchError(status: number, code: SmartApplyErrorCodeLiterals): boolean {
    if (!this.error || this.error.errors.length > 1) return false;
    const error = this.error.errors[0];
    return (
      typeof error === "object" &&
      "extensions" in error &&
      typeof error.extensions === "object" &&
      !!error.extensions &&
      "code" in error.extensions &&
      typeof error.extensions.code === "string" &&
      "status" in error.extensions &&
      typeof error.extensions.status === "number" &&
      error.extensions.status === status &&
      error.extensions.code === code
    );
  }

  static init() {
    return new GrafferGqlResult<any>(undefined, undefined);
  }
}
